const WINDOW_NAME_PREFIX = '__SPXG_CHAIN_SNAPS_V2__';
const LEGACY_WINDOW_NAME_PREFIX = '__SPXG_CHAIN_SNAPS_V1__';

function num(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function compactCell(cell = {}) {
  const compact = [
    num(cell.g),
    num(cell.d),
    num(cell.vol),
    num(cell.nv),
    num(cell.bv),
    num(cell.sv),
    num(cell.gb),
    num(cell.gs),
    num(cell.db),
    num(cell.ds),
    num(cell.dbBull),
    num(cell.dbBear),
    num(cell.dsBull),
    num(cell.dsBear),
  ];
  const hasSplitFields = ['cnv', 'pnv', 'cvol', 'pvol'].every((field) => (
    Number.isFinite(Number(cell[field]))
  ));
  if (hasSplitFields) {
    compact.push(
      num(cell.cnv),
      num(cell.pnv),
      num(cell.cvol),
      num(cell.pvol),
      cell.hasLegSplit === true,
    );
  }
  return compact;
}

function expandCell(compact = []) {
  const cell = {
    g: num(compact[0]),
    d: num(compact[1]),
    vol: num(compact[2]),
    nv: num(compact[3]),
    bv: num(compact[4]),
    sv: num(compact[5]),
    gb: num(compact[6]),
    gs: num(compact[7]),
    db: num(compact[8]),
    ds: num(compact[9]),
    dbBull: num(compact[10]),
    dbBear: num(compact[11]),
    dsBull: num(compact[12]),
    dsBear: num(compact[13]),
  };
  if (compact.length >= 18) {
    cell.cnv = num(compact[14]);
    cell.pnv = num(compact[15]);
    cell.cvol = num(compact[16]);
    cell.pvol = num(compact[17]);
    cell.hasLegSplit = compact.length >= 19 ? compact[18] === true : true;
  } else {
    cell.hasLegSplit = false;
  }
  return cell;
}

export function serializeChainSnapsForWindowName(snaps = []) {
  return JSON.stringify(
    snaps.map((snap) => [
      num(snap?.t),
      Number.isFinite(Number(snap?.spot)) ? Number(snap.spot) : null,
      Number.isFinite(Number(snap?.hydrationFrom)) ? Number(snap.hydrationFrom) : null,
      Number.isFinite(Number(snap?.hydrationTo)) ? Number(snap.hydrationTo) : null,
      Boolean(snap?.hydrationSeam),
      Boolean(snap?.hydrationBoundaryTail),
      Object.entries(snap?.byStrike || {}).map(([strike, cell]) => [
        Number(strike),
        ...compactCell(cell),
      ]),
    ])
  );
}

export function deserializeChainSnapsFromWindowName(serialized) {
  const parsed = JSON.parse(serialized);
  if (!Array.isArray(parsed)) return null;

  const snaps = parsed.map((snap) => {
    if (!Array.isArray(snap) || snap.length < 5) return null;

    const compactLayout = snap.length >= 7;
    const rows = compactLayout ? snap[6] : snap[4];

    const byStrike = {};
    for (const row of rows || []) {
      if (!Array.isArray(row) || row.length < 2) continue;
      const strike = Number(row[0]);
      if (!Number.isFinite(strike)) continue;
      byStrike[strike] = expandCell(row.slice(1));
    }

    const t = Number(snap[0]);
    if (!Number.isFinite(t)) return null;

    return {
      t,
      spot: Number.isFinite(Number(snap[1])) ? Number(snap[1]) : null,
      hydrationFrom: Number.isFinite(Number(snap[2])) ? Number(snap[2]) : null,
      hydrationTo: Number.isFinite(Number(snap[3])) ? Number(snap[3]) : null,
      hydrationSeam: compactLayout ? Boolean(snap[4]) : false,
      hydrationBoundaryTail: compactLayout ? Boolean(snap[5]) : false,
      byStrike,
    };
  });

  return snaps.every(Boolean) ? snaps : null;
}

export function readWindowNameChainSnaps() {
  if (typeof window === 'undefined') return null;
  const raw = window.name || '';
  const prefix = raw.startsWith(WINDOW_NAME_PREFIX)
    ? WINDOW_NAME_PREFIX
    : (raw.startsWith(LEGACY_WINDOW_NAME_PREFIX) ? LEGACY_WINDOW_NAME_PREFIX : null);
  if (!prefix) return null;

  try {
    return deserializeChainSnapsFromWindowName(raw.slice(prefix.length));
  } catch {
    return null;
  }
}

export function writeWindowNameChainSnaps(snaps = []) {
  if (typeof window === 'undefined') return;

  try {
    window.name = `${WINDOW_NAME_PREFIX}${serializeChainSnapsForWindowName(snaps)}`;
  } catch {
    // Non-fatal: localStorage remains the durable path for same-origin reloads.
  }
}
